import { useState, useEffect } from "react"
import { getDatabase, ref, onValue, push, set, remove } from "firebase/database"
import { Check, X, AlertCircle } from "lucide-react"

// Replace external CSS import with inline styles
const styles = {
  adminProductApproval: {
    padding: "1.5rem",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  sectionHeader: {
    marginBottom: "1.5rem",
  },
   sectionHeaderTitle: {
    color: "var(--text-primary)",
  },
  sectionHeaderPara:{
   color: "var(--text-primary)",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
  },
  spinner: {
    border: "4px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "50%",
    borderTop: "4px solid #3498db",
    width: "30px",
    height: "30px",
    animation: "spin 1s linear infinite",
  },
  noProducts: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "2rem",
    backgroundColor: "#f9f9f9",
    borderRadius: "0.5rem",
  },
  productApprovalList: {
    overflowX: "auto",
  },
  approvalTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHead: {
    backgroundColor: "#f5f5f5",
    textAlign: "left",
  },
    tableCell: {
    padding: "8px",
    border: "1px solid #ccc",
    color: "#000", // explicitly set text color to black
    backgroundColor: "#fff", // optional: white background
  },
  productInfo: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  productThumbnail: {
    width: "50px",
    height: "50px",
    objectFit: "cover",
    borderRadius: "4px",
  },
  pdfIcon: {
    width: "50px",
    height: "50px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    fontWeight: "bold",
  },
  actionButtons: {
    display: "flex",
    gap: "0.5rem",
  },
  approveBtn: {
    padding: "0.5rem 1rem",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  productApprovalModal: {
    backgroundColor: "white",
    borderRadius: "8px",
    width: "90%",
    maxWidth: "800px",
    maxHeight: "90vh",
    overflow: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
    borderBottom: "1px solid #eee",
  },
 closeModalBtn: {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "1.5rem",
  color: "#000",
  transition: "color 0.3s ease",
},


  modalContent: {
    padding: "1.5rem",
  },
  productPreview: {
    marginBottom: "1.5rem",
  },
  productImage: {
    maxWidth: "100%",
    maxHeight: "300px",
    objectFit: "contain",
    borderRadius: "4px",
  },
  pdfPreview: {
    padding: "1rem",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    textAlign: "center",
  },
  productDetails: {
    marginBottom: "1.5rem",
  },
  productDescription: {
    marginTop: "1rem",
  },
  approvalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "1rem",
    marginTop: "1rem",
  },
  rejectButton: {
    padding: "0.5rem 1rem",
    backgroundColor: "#f44336",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  approveButton: {
    padding: "0.5rem 1rem",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
}

const AdminProductApproval = ({ selectedBrand = "" }) => {
  const [pendingProducts, setPendingProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const gifUrl = "/Images/New notification.gif"

  // Function to send notifications
  const sendNotification = async (userId, title, message, brandName) => {
    if (!userId) return

    const db = getDatabase()
    const notificationRef = ref(db, `user_notifications/NewProduct/${userId}`)
    const newNotificationRef = push(notificationRef)

    const newNotification = {
      title,
      message,
      gifUrl,
      brandName,
      timestamp: new Date().toISOString(),
      seen: false,
    }

    try {
      await set(newNotificationRef, newNotification)
      console.log("Notification sent successfully")
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  // Fetch pending products
  useEffect(() => {
    const fetchPendingProducts = async () => {
      try {
        setLoading(true)
        const db = getDatabase()
        const productsRef = ref(db, "pendingProducts") // Changed from newProducts to pendingProducts

        onValue(productsRef, (snapshot) => {
          if (snapshot.exists()) {
            const allProducts = []

            snapshot.forEach((userSnapshot) => {
              const userId = userSnapshot.key
              const userData = userSnapshot.val()

              Object.entries(userData).forEach(([productId, product]) => {
                // Only include products that match the selected brand (if any)
                if (!selectedBrand || product.brandName === selectedBrand) {
                  allProducts.push({
                    ...product,
                    userId,
                    productId,
                  })
                }
              })
            })

            setPendingProducts(allProducts)
          } else {
            setPendingProducts([])
          }
          setLoading(false)
        })
      } catch (error) {
        console.error("Error fetching pending products:", error)
        setLoading(false)
      }
    }

    fetchPendingProducts()
  }, [selectedBrand])

  // Handle product approval
  const handleApproval = async (product, isApproved) => {
    try {
      const db = getDatabase()
      const pendingProductRef = ref(db, `pendingProducts/${product.userId}/${product.productId}`)
  
      if (isApproved) {
        // If approved, move the product to the approved products collection
        const approvedProductRef = ref(db, `newProducts/${product.userId}/${product.productId}`)
  
        // Copy the product data to the approved products collection
        await set(approvedProductRef, {
          ...product,
          status: "approved",
          approved: true,
          approvalDate: new Date().toISOString(),
        })
  
        // Send notification to the supplier who added the product
        if (product.userId) {
          const notificationMessage = `New Product ${product.name} has been added to your list by: ${product.brandName}`
          await sendNotification(
            product.userId,
            "New Product",
            notificationMessage,
            product.brandName,
          )
        }
      // } else {
      //   // If rejected, notify only the supplier
      //   if (product.userId) {
      //     await sendNotification(
      //       product.userId,
      //       "Product Rejected",
      //       `Your product "${product.name}" has been rejected. Please contact admin for more details.`,
      //       product.brandName,
      //     )
      //   }
      }
  
      // Remove from pending products regardless of approval or rejection
      await remove(pendingProductRef)
  
      // Close modal
      setShowModal(false)
      setSelectedProduct(null)
  
      // Products list will update automatically via the onValue listener
    } catch (error) {
      console.error("Error updating product status:", error)
    }
  }

  // Open confirmation modal
  const openConfirmationModal = (product) => {
    setSelectedProduct(product)
    setShowModal(true)
  }

  // Close modal
  const closeModal = () => {
    setShowModal(false)
    setSelectedProduct(null)
  }

  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading pending products...</p>
      </div>
    )
  }

  return (
    <div style={styles.adminProductApproval}>
     <div style={styles.sectionHeader}>
  <h2 style={styles.sectionHeaderTitle}>Product Approval</h2>
  <p style={styles.sectionHeaderPara}>Review and approve new products added by suppliers</p>
</div>


      {pendingProducts.length === 0 ? (
        <div style={styles.noProducts}>
          <AlertCircle size={24} />
          <p>No pending products to approve</p>
        </div>
      ) : (
        <div style={styles.productApprovalList}>
          <table style={styles.approvalTable}>
            <thead style={styles.tableHead}>
              <tr>
                <th style={styles.tableCell}>Product</th>
                <th style={styles.tableCell}>Brand</th>
                <th style={styles.tableCell}>Category</th>
                <th style={styles.tableCell}>Date Added</th>
                <th style={styles.tableCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingProducts.map((product) => (
                <tr key={product.productId}>
                  <td style={styles.tableCell}>
                    <div style={styles.productInfo}>
                      {product.fileType === "image" && (
                        <img
                          src={product.fileUrl || "/placeholder.svg"}
                          alt={product.name}
                          style={styles.productThumbnail}
                        />
                      )}
                      {product.fileType === "pdf" && <div style={styles.pdfIcon}>PDF</div>}
                      <span>{product.name}</span>
                    </div>
                  </td>
                  <td style={styles.tableCell}>{product.brandName}</td>
                  <td style={styles.tableCell}>{product.category}</td>
                  <td style={styles.tableCell}>{formatDate(product.timestamp)}</td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      <button style={styles.approveBtn} onClick={() => openConfirmationModal(product)}>
                        Review
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Modal */}
      {showModal && selectedProduct && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.productApprovalModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>Review Product</h3>
              <button style={styles.closeModalBtn} onClick={closeModal}>
                <X />
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.productPreview}>
                {selectedProduct.fileType === "image" ? (
                  <img
                    src={selectedProduct.fileUrl || "/placeholder.svg"}
                    alt={selectedProduct.name}
                    style={styles.productImage}
                  />
                ) : (
                  <div style={styles.pdfPreview}>
                    <a href={selectedProduct.fileUrl} target="_blank" rel="noopener noreferrer">
                      View PDF
                    </a>
                  </div>
                )}
              </div>

              <div style={styles.productDetails}>
                <h4>{selectedProduct.name}</h4>
                <p>
                  <strong>Brand:</strong> {selectedProduct.brandName}
                </p>
                <p>
                  <strong>Category:</strong> {selectedProduct.category}
                </p>
                {selectedProduct.price && (
                  <p>
                    <strong>Price:</strong> ₹{selectedProduct.price}
                  </p>
                )}
                {selectedProduct.location && (
                  <p>
                    <strong>Location:</strong> {selectedProduct.location}
                  </p>
                )}
                <p>
                  <strong>Added on:</strong> {formatDate(selectedProduct.timestamp)}
                </p>
                <div style={styles.productDescription}>
                  <strong>Description:</strong>
                  <p>{selectedProduct.productDetails}</p>
                </div>
              </div>

              <div style={styles.approvalActions}>
                <button style={styles.rejectButton} onClick={() => handleApproval(selectedProduct, false)}>
                  <X size={16} />
                  Reject
                </button>
                <button style={styles.approveButton} onClick={() => handleApproval(selectedProduct, true)}>
                  <Check size={16} />
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminProductApproval;